import { useEffect, useRef } from 'react';
import { debug } from '@/lib/debug';

/**
 * Enterprise-grade session lifecycle manager.
 * 
 * Handles three scenarios that enterprise apps (Notion, Slack, Linear) solve:
 * 
 * 1. CHROME TAB DISCARDING: When Chrome kills an inactive tab to save memory,
 *    the page reloads when the user returns. We detect this via `document.wasDiscarded`
 *    and restore the user's last route from sessionStorage.
 * 
 * 2. STALE SESSION DETECTION: Tracks how long the tab was inactive.
 *    - < 30 min: Do nothing (data is fresh enough)
 *    - > 8 hours: Check for app version update, apply if available
 *    This gives users a "first access of the day" update experience.
 * 
 * 3. ROUTE PERSISTENCE: Saves the current route to sessionStorage so that
 *    if Chrome discards the tab or the page reloads, the user returns to
 *    exactly where they were (not the homepage).
 * 
 * This hook does NOT:
 * - Force-reload while the user is actively working
 * - Reload on every tab switch
 * - Interfere with Supabase auth token refresh
 */

const LAST_ACTIVE_KEY = 'vb-last-active-timestamp';
const LAST_ROUTE_KEY = 'vb-last-route';
const STALE_SESSION_THRESHOLD_MS = 8 * 60 * 60 * 1000; // 8 hours

export function useSessionLifecycle() {
  const isFirstRun = useRef(true);

  useEffect(() => {
    // ── 1. Detect Chrome Tab Discard ──
    // Chrome sets document.wasDiscarded = true when a tab was killed and reloaded
    if ((document as any).wasDiscarded) {
      debug.log('[SessionLifecycle] Tab was discarded by Chrome  -  restoring state');
      const savedRoute = sessionStorage.getItem(LAST_ROUTE_KEY);
      if (savedRoute && savedRoute !== window.location.pathname && savedRoute !== '/') {
        debug.log(`[SessionLifecycle] Restoring route: ${savedRoute}`);
        // Use replaceState to avoid adding to history
        window.history.replaceState(null, '', savedRoute);
        // Force React Router to pick up the new URL
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }

    // ── 2. Stale Session Detection ──
    const checkStaleSession = () => {
      const lastActive = localStorage.getItem(LAST_ACTIVE_KEY);
      if (!lastActive) return false;

      const elapsed = Date.now() - parseInt(lastActive, 10);
      const isStale = elapsed > STALE_SESSION_THRESHOLD_MS;

      if (isStale) {
        debug.log(`[SessionLifecycle] Session stale (${Math.round(elapsed / 3600000)}h idle)  -  checking for updates`);
      }

      return isStale;
    };

    // On initial mount, check if this is a "first access of the day" scenario
    if (isFirstRun.current) {
      isFirstRun.current = false;
      const isStale = checkStaleSession();

      if (isStale) {
        // Check if there's a new version available
        // The version check is handled by useVersionCheck  -  we just need to
        // make sure it runs. If the version changed, it will handle the reload.
        // If the version is the same, we just continue normally.
        debug.log('[SessionLifecycle] First access after long idle  -  version check will handle update if needed');
      }
    }

    // ── 3. Track Activity ──
    // Update the last-active timestamp periodically and on visibility change
    const updateLastActive = () => {
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    };

    // Update immediately
    updateLastActive();

    // Update on visibility change (when tab becomes visible)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const lastActive = localStorage.getItem(LAST_ACTIVE_KEY);
        const elapsed = lastActive ? Date.now() - parseInt(lastActive, 10) : 0;

        if (elapsed > STALE_SESSION_THRESHOLD_MS) {
          debug.log(`[SessionLifecycle] Returning after ${Math.round(elapsed / 3600000)}h  -  will check for updates`);
          // Don't force reload here  -  let the version check system handle it
          // The user might be in the middle of something in another tab
        }

        updateLastActive();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    // Update every 5 minutes while active
    const activityInterval = setInterval(updateLastActive, 5 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(activityInterval);
    };
  }, []);
}

/**
 * Hook to persist the current route to sessionStorage.
 * Call this from your router/layout component.
 * 
 * When Chrome discards a tab and reloads it, the URL is preserved by the browser,
 * but React Router may not pick it up correctly in all cases. This provides a fallback.
 */
export function useRoutePersistence(pathname: string) {
  useEffect(() => {
    if (pathname && pathname !== '/') {
      sessionStorage.setItem(LAST_ROUTE_KEY, pathname);
    }
  }, [pathname]);
}
